import { useState } from "react";

import type { ReminderResponse } from "../../shared/api/client";
import { useLocale, useT } from "../../shared/i18n";
import {
  ActionBar,
  Banner,
  BottomSheet,
  Button,
  Card,
  DetailHeader,
  Dialog,
  type SheetAction,
} from "../../shared/ui";
import { RescheduleSheet } from "./RescheduleSheet";
import { reminderStatus } from "./status";
import { clockLabel, dayLabel, localNow, type LocalDateTime } from "./time";

type Pending = "stop" | "delete" | null;

/** One reminder, full screen, with the commands its status allows. */
export function ReminderDetail({
  reminder,
  busy,
  failed,
  onBack,
  onEdit,
  onReschedule,
  onTransition,
}: {
  reminder: ReminderResponse;
  busy: boolean;
  failed: boolean;
  onBack: () => void;
  onEdit: () => void;
  /** Resolves true once the new time is saved. */
  onReschedule: (localSchedule: LocalDateTime) => Promise<boolean>;
  onTransition: (operation: "complete" | "resume" | "remove") => Promise<void>;
}) {
  const t = useT();
  const { locale } = useLocale();
  const status = reminderStatus(reminder);
  const repeating = reminder.recurrence !== "once";
  const [pending, setPending] = useState<Pending>(null);
  const [moving, setMoving] = useState(false);
  const [menu, setMenu] = useState(false);
  const when = `${dayLabel(locale, reminder.local_time, localNow(reminder.timezone))}, ${clockLabel(reminder.local_time)}`;
  const more: SheetAction[] = [
    ...(status === "active" && repeating
      ? [
          {
            id: "stop",
            label: t("reminders.stop"),
            onSelect: () => {
              setPending("stop");
            },
          },
        ]
      : []),
    {
      id: "delete",
      label: t("reminders.delete"),
      danger: true,
      onSelect: () => {
        setPending("delete");
      },
    },
  ];
  const primary =
    status === "stopped" ? (
      <Button
        loading={busy}
        onClick={() => {
          void onTransition("resume");
        }}
      >
        {t("reminders.resume")}
      </Button>
    ) : status === "sent" ? null : (
      <Button
        disabled={busy}
        onClick={() => {
          setMoving(true);
        }}
      >
        {t("reminders.reschedule")}
      </Button>
    );
  return (
    <div className="fixed inset-0 z-40 flex flex-col overflow-y-auto bg-canvas text-fg">
      <DetailHeader
        title={reminder.title}
        largeTitle
        onBack={onBack}
        backLabel={t("app.back")}
        trailing={
          <Button
            variant="tinted"
            size="xs"
            onClick={() => {
              setMenu(true);
            }}
          >
            {t("reminders.more")}
          </Button>
        }
      />
      <main className="flex flex-1 flex-col gap-gutter p-4">
        {status === "missed" && (
          <Banner tone="warning" title={t("reminders.missed.title")}>
            {t("reminders.missed.body")}
          </Banner>
        )}
        {status === "stopped" && (
          <Banner tone="info" title={t("reminders.stopped.title")}>
            {t("reminders.stopped.body")}
          </Banner>
        )}
        {failed && <Banner tone="error">{t("record.error")}</Banner>}
        <Card flush>
          <dl>
            {[
              [t("reminders.when"), when],
              [t("reminders.repeat"), t(`reminders.${reminder.recurrence}`)],
              [t("reminders.timezone"), reminder.timezone],
            ].map(([label, value]) => (
              <div
                key={label}
                className="flex items-baseline justify-between gap-4 border-b border-line-subtle px-4 py-3.5 last:border-b-0"
              >
                <dt className="text-body text-fg-muted">{label}</dt>
                <dd className="text-right text-body font-medium text-fg">
                  {value}
                </dd>
              </div>
            ))}
          </dl>
        </Card>
      </main>
      <ActionBar>
        {status === "active" ? (
          <Button variant="secondary" disabled={busy} onClick={onEdit}>
            {t("reminders.edit")}
          </Button>
        ) : (
          <Button
            variant="dangerSoft"
            disabled={busy}
            onClick={() => {
              setPending("delete");
            }}
          >
            {t("reminders.delete")}
          </Button>
        )}
        {primary}
      </ActionBar>
      <BottomSheet
        open={menu}
        onOpenChange={setMenu}
        title={reminder.title}
        actions={more}
      />
      {status !== "sent" && status !== "stopped" && (
        <RescheduleSheet
          key={reminder.revision}
          reminder={reminder}
          open={moving}
          busy={busy}
          onOpenChange={setMoving}
          onSubmit={(value) => {
            void onReschedule(value).then((saved) => {
              if (saved) setMoving(false);
            });
          }}
        />
      )}
      <Dialog
        open={pending !== null}
        onOpenChange={(value) => {
          if (!value && !busy) setPending(null);
        }}
        title={t(
          pending === "stop"
            ? "reminders.stopConfirm.title"
            : "reminders.deleteConfirm.title",
        )}
        description={t(
          pending === "stop"
            ? "reminders.stopConfirm.body"
            : "reminders.deleteConfirm.body",
        )}
        footer={
          <>
            <Button
              variant="secondary"
              disabled={busy}
              onClick={() => {
                setPending(null);
              }}
            >
              {t("record.cancel")}
            </Button>
            <Button
              variant={pending === "delete" ? "danger" : "primary"}
              loading={busy}
              onClick={() => {
                void onTransition(
                  pending === "stop" ? "complete" : "remove",
                ).finally(() => {
                  setPending(null);
                });
              }}
            >
              {t(
                pending === "stop"
                  ? "reminders.stopConfirm.confirm"
                  : "record.deleteConfirm.confirm",
              )}
            </Button>
          </>
        }
      />
    </div>
  );
}
